/**
 * Hook for managing notification preferences
 * 
 * Provides CRUD operations for user notification settings including
 * email enablement, frequency, preferred time, and timezone.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  getNotificationPreferences,
  createNotificationPreferences,
  updateNotificationPreferences,
} from '../../api/notifications';
import type {
  NotificationPreference,
  NotificationPreferenceCreate,
  NotificationPreferenceUpdate,
} from '../../types/notification';
import { useAuth } from '../../contexts/AuthContext';

interface UseNotificationPreferencesResult {
  preferences: NotificationPreference | null;
  loading: boolean;
  error: string | null;
  updatePreferences: (data: NotificationPreferenceUpdate) => Promise<void>;
  refetch: () => Promise<void>;
}

export const useNotificationPreferences = (): UseNotificationPreferencesResult => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreference | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Keyed on the user's id, not the user object.
  //
  // This callback is an effect dependency, so anything that changes its identity re-runs the
  // fetch — and a fetch that sets loading back to true. AuthContext currently holds `user` in
  // state, so the reference is stable and that does not happen; but it is one refactor away
  // (`setUser({ ...user, x })` in a render path, or a context that rebuilds the object) from an
  // unbounded request loop against the API, with the page stuck on its skeleton the whole time.
  // The id is the only part of `user` this fetch actually depends on.
  const userId = user?.id ?? null;

  const fetchPreferences = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getNotificationPreferences();
      setPreferences(data);
      setError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load notification preferences';
      setError(errorMessage);
      
      // If preferences don't exist (404), create defaults
      if (err instanceof Error && err.message.includes('404')) {
        try {
          const defaultPreferences: NotificationPreferenceCreate = {
            user_id: userId,
            email_notifications_enabled: true,
            notification_frequency: 'daily',
            preferred_hour: 9,
            preferred_minute: 0,
            timezone: 'UTC',
          };
          const created = await createNotificationPreferences(defaultPreferences);
          setPreferences(created);
          setError(null);
        } catch (createErr) {
          console.error('Failed to create default preferences:', createErr);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updatePreferences = async (data: NotificationPreferenceUpdate): Promise<void> => {
    try {
      setError(null);
      const updated = await updateNotificationPreferences(data);
      setPreferences(updated);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update notification preferences';
      setError(errorMessage);
      throw err; // Re-throw so form can handle error
    }
  };

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    refetch: fetchPreferences,
  };
};
